import { useState, useEffect, useRef } from 'react';
import { useCreateClientWithOnboarding, useUpdateClient } from '../../hooks/useCreateClient';
import { ClientOnboardingFormData, ClientType } from '../../types';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import CompanyDetailsTab from './CompanyDetailsTab';
import ConfigurationTab from './ConfigurationTab';
import { FIELD_VALIDATORS } from '../../utils/validationHelpers';

const initialFormData: ClientOnboardingFormData = {
  // Company Details
  companyName: '',
  registrationNumber: '',
  gstNumber: '',
  panNumber: '',
  address: '',
  city: '',
  state: '',
  pinCode: '',
  country: 'India',
  contactPersonName: '',
  designation: '',
  email: '',
  phone: '',
  alternatePhone: '',
  
  // Product & Storage Configuration
  productCategoryIds: [],
  productTypeIds: [],
  storageRequirementIds: [], // Changed to array
  handlingInstructions: '',
  
  // Client Contract
  clientType: 'per_pallet' as ClientType,
  palletCapacity: '',
  sqftCapacity: '',
  status: 'active' as 'active' | 'inactive',
};

interface CreateClientProps {
  /** Present = edit mode; omit for create mode. */
  clientId?: number;
  /** Close the overlay without saving (Cancel / X). */
  onClose: () => void;
  /** Called after a successful create/update. */
  onSuccess: () => void;
}

export default function CreateClient({ clientId, onClose, onSuccess }: CreateClientProps) {
  const isEdit = clientId != null;
  const { showToast } = useToast();
  const { user } = useAuth();
  const activeWarehouseId = user?.activeWarehouseId ?? user?.warehouseId ?? undefined;
  
  const [formData, setFormData] = useState<ClientOnboardingFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formBodyRef = useRef<HTMLDivElement>(null);

  const createClientMutation = useCreateClientWithOnboarding();
  const updateClientMutation = useUpdateClient();

  // Fetch client data for editing
  const { data: clientData, isLoading: clientLoading, error: clientError } = useQuery({
    queryKey: ['client', clientId],
    queryFn: async () => {
      if (clientId == null) return null;
      try {
        const response = await api.get(`/clients/${clientId}`);
        const responseData = response as any;
        // API returns: { success, statusCode, message, data: {...}, timestamp }
        // So we need to access responseData.data
        if (!responseData) {
          return null;
        }
        
        // Check if data is nested or direct
        const clientInfo = responseData.data || responseData;
        
        if (!clientInfo) {
          console.error('Invalid response structure:', responseData);
          return null;
        }
        
        console.log('Extracted client info:', clientInfo);
        return clientInfo;
      } catch (error: any) {
        console.error('Error fetching client:', error);
        if (error.response?.status === 404) {
          showToast('Client not found. Redirecting to client list.', 'error');
          setTimeout(onClose, 2000);
        }
        return null; // Return null instead of throwing
      }
    },
    enabled: isEdit,
  });

  // Populate form data when editing
  useEffect(() => {
    if (isEdit && clientData) {
      console.log('Populating form with client data:', clientData);
      console.log('Product types from API:', clientData.productTypes);
      
      const productTypeIds = clientData.productTypes?.map((pt: any) => pt.id) || [];
      console.log('Mapped productTypeIds:', productTypeIds);
      
      setFormData({
        companyName: clientData.companyName || '',
        registrationNumber: clientData.registrationNumber || '',
        gstNumber: clientData.gstNumber || '',
        panNumber: clientData.panNumber || '',
        address: clientData.address || '',
        city: clientData.city || '',
        state: clientData.state || '',
        pinCode: clientData.pinCode || '',
        country: clientData.country || 'India',
        contactPersonName: clientData.contactPersonName || '',
        designation: clientData.designation || '',
        email: clientData.email || '',
        phone: clientData.phone || '',
        alternatePhone: clientData.alternatePhone || '',
        productCategoryIds: clientData.productCategories?.map((pc: any) => pc.id) || [],
        productTypeIds: productTypeIds,
        storageRequirementIds: clientData.storageRequirements?.map((sr: any) => sr.id) || [], // Changed to array
        handlingInstructions: clientData.handlingInstructions || '',
        clientType: clientData.clientType || 'per_pallet',
        palletCapacity: clientData.palletCapacity || 0,
        sqftCapacity: clientData.sqftAssigned || 0,
        status: clientData.status === 'inactive' ? 'inactive' : 'active',
        warehouseId: clientData.warehouseId ?? activeWarehouseId,
      });
      
      console.log('Form populated successfully with productTypeIds:', productTypeIds);
    }
  }, [activeWarehouseId, clientData, isEdit]);

  // Handle input changes
  const handleInputChange = (field: keyof ClientOnboardingFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validation functions. Each returns its own error map (no setState) so the
  // caller can merge company + configuration errors in a single setErrors call.
  const validateCompanyTab = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    // Company Name - using validator
    const companyNameError = FIELD_VALIDATORS.companyName(formData.companyName, true);
    if (companyNameError) newErrors.companyName = companyNameError;

    // Registration Number (optional) - using validator
    if (formData.registrationNumber && formData.registrationNumber.trim()) {
      const regError = FIELD_VALIDATORS.registrationNumber(formData.registrationNumber, false);
      if (regError) newErrors.registrationNumber = regError;
    }

    // GST Number - using validator
    const gstError = FIELD_VALIDATORS.gstNumber(formData.gstNumber, true);
    if (gstError) newErrors.gstNumber = gstError;

    // PAN Number - using validator
    const panError = FIELD_VALIDATORS.panNumber(formData.panNumber, true);
    if (panError) newErrors.panNumber = panError;

    // Address - using validator
    const addressError = FIELD_VALIDATORS.address(formData.address, true);
    if (addressError) newErrors.address = addressError;

    // City - using validator
    const cityError = FIELD_VALIDATORS.cityName(formData.city, true);
    if (cityError) newErrors.city = cityError;

    // State
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    // PIN Code - using validator
    const pinError = FIELD_VALIDATORS.pinCode(formData.pinCode, true);
    if (pinError) newErrors.pinCode = pinError;

    // Country
    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    // Contact Person Name - using validator
    const contactPersonError = FIELD_VALIDATORS.contactPersonName(formData.contactPersonName, true);
    if (contactPersonError) newErrors.contactPersonName = contactPersonError;

    // Designation - using validator (optional)
    if (formData.designation && formData.designation.trim()) {
      const designationError = FIELD_VALIDATORS.designation(formData.designation, false);
      if (designationError) newErrors.designation = designationError;
    }

    // Email - using validator
    const emailError = FIELD_VALIDATORS.email(formData.email, true);
    if (emailError) newErrors.email = emailError;

    // Mobile Number - using validator
    const mobileError = FIELD_VALIDATORS.mobileNumber(formData.phone, true);
    if (mobileError) newErrors.phone = mobileError;

    // Alternate Mobile Number (optional now)
    if (formData.alternatePhone && formData.alternatePhone.trim()) {
      const altMobileError = FIELD_VALIDATORS.mobileNumber(formData.alternatePhone, false);
      if (altMobileError) {
        newErrors.alternatePhone = altMobileError;
      } else if (formData.alternatePhone === formData.phone) {
        newErrors.alternatePhone = 'Alternate mobile number must be different from primary mobile number';
      }
    }

    return newErrors;
  };

  const validateConfigurationTab = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientType) newErrors.clientType = 'Client type is required';

    if (formData.clientType === 'per_pallet' || formData.clientType === 'both') {
      const capacity = typeof formData.palletCapacity === 'string' ? Number(formData.palletCapacity) : formData.palletCapacity;
      if (!formData.palletCapacity || capacity <= 0 || isNaN(capacity)) {
        newErrors.palletCapacity = 'Pallet capacity must be greater than 0';
      }
    }

    if (formData.clientType === 'per_sqft' || formData.clientType === 'both') {
      const capacity = typeof formData.sqftCapacity === 'string' ? Number(formData.sqftCapacity) : formData.sqftCapacity;
      if (!formData.sqftCapacity || capacity <= 0 || isNaN(capacity)) {
        newErrors.sqftCapacity = 'Square feet assigned must be greater than 0';
      }
    }

    return newErrors;
  };

  // Submit form
  const handleSubmit = async () => {
    // Validate both sections and merge errors in ONE setErrors call so the
    // company and configuration errors don't overwrite each other.
    const combinedErrors = {
      ...validateCompanyTab(),
      ...validateConfigurationTab(),
    };
    setErrors(combinedErrors);

    if (Object.keys(combinedErrors).length > 0) {
      // Give visible feedback instead of silently doing nothing
      showToast(
        Object.values(combinedErrors)[0] ||
          'Please fill all required fields correctly.',
        'error',
      );
      formBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      if (isEdit) {
        // Update existing client
        await updateClientMutation.mutateAsync({
          id: clientId!,
          data: {
            warehouseId: activeWarehouseId,
            companyName: formData.companyName,
            registrationNumber: formData.registrationNumber,
            gstNumber: formData.gstNumber,
            panNumber: formData.panNumber,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            pinCode: formData.pinCode,
            country: formData.country,
            contactPersonName: formData.contactPersonName,
            designation: formData.designation,
            email: formData.email,
            phone: formData.phone,
            alternatePhone: formData.alternatePhone,
            clientType: formData.clientType,
            palletCapacity: typeof formData.palletCapacity === 'string' ? Number(formData.palletCapacity) : formData.palletCapacity,
            sqftAssigned: typeof formData.sqftCapacity === 'string' ? Number(formData.sqftCapacity) : formData.sqftCapacity, // Map sqftCapacity to sqftAssigned for backend
            productCategoryIds: formData.productCategoryIds,
            productTypeIds: formData.productTypeIds,
            storageRequirementIds: formData.storageRequirementIds, // Changed to array
            handlingInstructions: formData.handlingInstructions,
            status: formData.status,
          }
        });
      } else {
        // Create new client
        const submitData = {
          ...formData,
          warehouseId: activeWarehouseId,
          phone: formData.phone,
          palletCapacity: typeof formData.palletCapacity === 'string' ? Number(formData.palletCapacity) : formData.palletCapacity,
          sqftAssigned: typeof formData.sqftCapacity === 'string' ? Number(formData.sqftCapacity) : formData.sqftCapacity, // Map to backend field for creation
          status: formData.status,
        };
        await createClientMutation.mutateAsync(submitData);
      }
      
      showToast(`Client ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
      onSuccess();
    } catch (error: any) {
      console.error('Error saving client:', error);
      
      // Handle backend validation errors (errors array)
      if (error?.errors && Array.isArray(error.errors)) {
        const backendErrors: Record<string, string> = {};
        error.errors.forEach((err: any) => {
          if (typeof err === 'string') {
            // Handle string errors - map backend field names to frontend field names
            const lowerErr = err.toLowerCase();
            if (lowerErr.includes('phone') && lowerErr.includes('alternate')) {
              backendErrors.alternatePhone = err;
            } else if (lowerErr.includes('phone')) {
              backendErrors.phone = err;
            } else if (lowerErr.includes('email')) {
              backendErrors.email = err;
            } else {
              backendErrors.general = err;
            }
          } else if (err.field) {
            // Map backend field names to frontend field names
            const fieldMap: Record<string, string> = {
              'phone': 'phone',
              'alternatePhone': 'alternatePhone',
            };
            const frontendField = fieldMap[err.field] || err.field;
            backendErrors[frontendField] = err.message;
          }
        });
        setErrors(backendErrors);
        
        // Show first error as toast
        const firstError = Object.values(backendErrors)[0];
        if (firstError) {
          showToast(firstError, 'error');
        }
      } else {
        // Handle regular error message
        const errorMessage = error?.message || `Failed to ${isEdit ? 'update' : 'create'} client`;
        showToast(errorMessage, 'error');
      }
    }
  };

  if (isEdit && clientLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="flex items-center justify-center">
          <div className="text-center bg-white p-10 rounded-2xl shadow-2xl border border-gray-200">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-200 border-t-blue-600 mx-auto mb-6"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-8 h-8 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
            </div>
            <p className="text-gray-800 font-semibold text-lg">Loading client data...</p>
            <p className="text-gray-500 text-sm mt-2">Please wait while we prepare the form</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle client not found error
  if (isEdit && clientError) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="flex items-center justify-center">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl border-2 border-red-200 overflow-hidden">
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white">Client Not Found</h2>
            </div>
            <div className="p-8 text-center">
              <p className="text-gray-600 mb-6">
                The client you're trying to edit doesn't exist or you don't have permission to access it.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center space-x-2 mx-auto"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Client List</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end lg:items-center justify-center z-50 p-3 lg:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] lg:max-h-[92vh] overflow-hidden flex flex-col animate-slide-up lg:animate-none">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-4 lg:px-8 lg:py-5 text-white flex items-start justify-between flex-shrink-0">
          <div className="flex items-center min-w-0">
            <div className="p-2.5 bg-white/20 rounded-xl mr-3 flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h1 className="text-xl lg:text-2xl font-bold text-white">
                {isEdit ? 'Edit Client' : 'Add Client'}
              </h1>
              <p className="text-brand-100 text-sm mt-0.5">
                {isEdit ? 'Update client information and configuration' : 'Add a new client to the warehouse management system'}
              </p>
              {activeWarehouseId && (
                <p className="text-brand-100/90 text-xs mt-1">
                  Client will be scoped to warehouse #{activeWarehouseId}.
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            title="Close"
            className="p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Form Body — single page, both sections stacked */}
        <div ref={formBodyRef} className="overflow-y-auto flex-1 min-h-0 p-4 lg:p-8 bg-white space-y-8">
          <CompanyDetailsTab
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
          />

          <div className="border-t-2 border-gray-200" />

          <ConfigurationTab
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
            clientId={clientId}
          />
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between items-center px-4 py-4 lg:px-8 bg-white border-t-2 border-gray-200 flex-shrink-0">
                <button
                  onClick={onClose}
                  className="px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-semibold shadow-sm hover:shadow-md transform hover:scale-105 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Cancel</span>
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={handleSubmit}
                    disabled={createClientMutation.isPending || updateClientMutation.isPending}
                    className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
                  >
                    {createClientMutation.isPending || updateClientMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>{isEdit ? 'Updating...' : 'Creating...'}</span>
                      </>
                    ) : (
                      <>
                        <span>{isEdit ? 'Update Client' : 'Create Client'}</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
        </div>
      </div>
  );
}
